import { useState, useCallback } from 'react'

import { initRequestParam } from 'constants/constants'

import { useAuthContext } from 'AuthProvider'
import _ from 'lodash'
import { useApiCacheStore } from 'store/apiCacheStore'
import { normalizeDate } from 'utils/convertFromLocalDate'
import { getAPI } from 'utils/getAPI'
// import { useCurrentStatusStore } from 'store/currentStatusStore'


import type {
  GetMstCenterSupplierCenterSupplierGetRequest,
  ResponseResult,
  ResponseUpdate,
  MstCenterSupplierUpdateCenterSupplierUpdatePostRequest,
  CenterSupplier,
  MstCenterSupplier,
} from 'api-client'
import type { ErrorResponse } from 'types/types'

export interface MstCenterSupplierSeq extends MstCenterSupplier{
  id: string
}

export interface MstCenterSuppliersSeq extends CenterSupplier{
  list: MstCenterSupplierSeq[]
}

export const useCenterSupplier = () => {
  const [isLoading, setIsLoading] = useState(false)
  const [isUpdating, setIsUpdating] = useState(false)
  const [result, setResult] = useState<ResponseResult>()
  
  const [centerSupplierSearch, setCenterSupplierSearch] = useState<CenterSupplier>(initRequestParam)
  const [centerSupplierSearchIsLoading, setCenterSupplierSearchIsLoading] = useState(false)
  const [centerSupplierSearchResult, setCenterSupplierSearchResult] = useState<ResponseResult | undefined>(undefined)
  const [centerSupplierUpdateResult, setCenterSupplierUpdateResult] = useState<ResponseUpdate | undefined>(undefined)
  
  // const { setCurrentStatuses } = useCurrentStatusStore()
  const { callApiWithCache } = useApiCacheStore()
  const { loginUserInfo } = useAuthContext()
  const systemDate = normalizeDate(loginUserInfo?.systemDate ?? Date.now())
  if (systemDate) systemDate.setHours(0, 0, 0, 0)

  const fetchCenterSupplierSearch = useCallback(async (searchParam: GetMstCenterSupplierCenterSupplierGetRequest, forceRefresh = false) => {
    setCenterSupplierSearch(initRequestParam)
    setCenterSupplierSearchResult(undefined)
    setCenterSupplierSearchIsLoading(true)
    setResult(undefined)
    setIsLoading(true)

    try {
      const response = await callApiWithCache(
        '/center_supplier',
        searchParam,
        async () => {
          const api = await getAPI()
          if (!api) throw new Error('API initialization failed')
          return api.getMstCenterSupplierCenterSupplierGet(searchParam)
        },
        systemDate,
        forceRefresh
      )

      const mappedData = {
        ...response.data,
        list: _.map(response.data.list, (value) => ({
          ...value,
          id: Math.random().toString(32).substring(2),
        }))
      }

      setCenterSupplierSearch(mappedData)
      setCenterSupplierSearchResult(response.result)
      // setCurrentStatuses(response.result.systemInfo?.sysCurrentStatus)
      
      // Return response để có thể dùng trực tiếp
      return {
        data: mappedData,
        result: response.result
      }
    } catch (error) {
      const errorResponse = error as ErrorResponse
      const errorResult = {
        status: 'error' as const,
        message: errorResponse.response && errorResponse.response.status === 422 ? '入力値に誤りがあります。' : `センター仕入先マスタの取得でエラーが発生しました。`,
        systemDate: null,
        pageNo: 0,
        systemInfo: null,
      }
      setCenterSupplierSearchResult(errorResult)
      throw error
    } finally {
      setCenterSupplierSearchIsLoading(false)
      setIsLoading(false)
    }
  }, [callApiWithCache])

  const fetchCenterSupplierUpdate = async (body: MstCenterSupplierUpdateCenterSupplierUpdatePostRequest): Promise<ResponseUpdate> => {
    setIsUpdating(true);
    setResult(undefined)
    try {
      const api = await getAPI()
      if (!api) throw new Error('API initialization failed')
      const response = await api.mstCenterSupplierUpdateCenterSupplierUpdatePost(body);
      
      setResult(response.result)
      setCenterSupplierUpdateResult(response);

      if (response.result.status !== 'success') {
        const error = new Error(response.result.message || '入力エラーがあります');
        Object.assign(error, {
            ...response
        });
        throw error;
      }

      // Clear cache for center_supplier after successful update
      const { clearCache } = useApiCacheStore.getState()
      clearCache('/center_supplier')
      
      return response;
    } catch (error) {
        if (error && typeof error === 'object' && 'result' in error) {
            const apiResponse = error as ResponseUpdate; 
            await new Promise<void>(resolve => {
                setCenterSupplierUpdateResult(apiResponse);
              resolve();
            });
            return await Promise.reject(apiResponse);
        }
        const { response } = error as any;
        const errorResponse = {
            errors: [],
            result: {
                status: 'error',
                message: response && response.status === 422 ? '入力値に誤りがあります。' : `センター仕入先マスタの更新でエラーが発生しました。`,
                systemDate: null,
                pageNo: 0,
                systemInfo: null,
            },
            updateCount: null,
            lastUpdateDatetime: null,
        };
        await new Promise<void>(resolve => {
            setCenterSupplierUpdateResult(errorResponse);
            setResult(errorResponse.result)
            resolve();
        });
        return await Promise.reject(errorResponse);

    } finally {
      setIsUpdating(false);
    }
  };


  return {
  	result,
  	isLoading,
  	isUpdating,
    
    centerSupplierSearch,
    fetchCenterSupplierSearch,
    centerSupplierSearchResult,
    centerSupplierSearchIsLoading,

    fetchCenterSupplierUpdate,
    centerSupplierUpdateResult,
  }
}